import { IDocument } from 'interfaces/documentation/document';
import { IDocumentTemplate } from 'interfaces/documentation/document-template';

/**
 * GERE6-473 — na Carteira de Identidade Nacional (CIN) o número de
 * identificação é o CPF, então quem envia a CIN não envia o documento de CPF.
 *
 * O atlas decide isso pelo campo "Tipo de documento" do template `identity`
 * (`Documents::IdentityFields.cin_document?`) e só dispensa o CPF com a CIN
 * **aprovada** (`Documentations::Audit#replaced_by_cin?`). Estas funções
 * repetem essa leitura para a secretaria não ver o CPF como pendência.
 *
 * O nome do campo é rótulo e chave ao mesmo tempo, igual ao atlas.
 */
const DOCUMENT_TYPE_FIELD = 'Tipo de documento';
const CIN = 'CIN';

export type CinStatus = 'approved' | 'pending' | 'none';

/**
 * - `waived_by_cin`: CIN aprovada; o atlas não exige mais o CPF.
 * - `waivable_by_cin`: CIN aguardando validação; se for recusada, o CPF volta
 *   a ser exigido, então a tela não pode dizer que já está dispensado.
 */
export type DocumentRequirement =
  | 'required'
  | 'optional'
  | 'waived_by_cin'
  | 'waivable_by_cin';

const isCinDocument = (
  document: IDocument,
  identityTemplateIds: ReadonlySet<string>
): boolean =>
  identityTemplateIds.has(document.template_id) &&
  (document.attribute_fields ?? []).some(
    field => field.name === DOCUMENT_TYPE_FIELD && field.value === CIN
  );

export const cinStatus = (
  documents: readonly IDocument[],
  templates: readonly IDocumentTemplate[]
): CinStatus => {
  const identityTemplateIds = new Set(
    templates
      .filter(template => template.kind === 'identity')
      .map(template => template.id)
  );
  const cins = documents.filter(document =>
    isCinDocument(document, identityTemplateIds)
  );

  if (cins.some(document => document.status === 'approved')) {
    return 'approved';
  }

  if (cins.some(document => document.status === 'pending')) {
    return 'pending';
  }

  return 'none';
};

export const documentRequirement = (
  template: IDocumentTemplate,
  required: boolean,
  cin: CinStatus
): DocumentRequirement => {
  if (!required) {
    return 'optional';
  }

  if (template.kind !== 'registration_number') {
    return 'required';
  }

  switch (cin) {
    case 'approved': {
      return 'waived_by_cin';
    }

    case 'pending': {
      return 'waivable_by_cin';
    }

    case 'none': {
      return 'required';
    }
  }
};
